// Copyright (c) 2025 Goffy. This file belongs to Goffy. All Rights Reserved.

import React from 'react';
import { Pressable, StyleSheet } from 'react-native';

import { View, Text } from '@/components/Themed';
import { t } from '@/i18n';
import appColors from '@/theme/colors';

type AppErrorBoundaryState = {
  hasError: boolean;
};

export class AppErrorBoundary extends React.Component<React.PropsWithChildren, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('AppErrorBoundary caught error', error);
    console.error('AppErrorBoundary error stack', error.stack);
    console.error('AppErrorBoundary component stack', info.componentStack);
    if (typeof window !== 'undefined' && window.location?.href) {
      console.error('AppErrorBoundary location', window.location.href);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false });
  };

  render() {
    if (this.state.hasError) {
      return (
        <View style={styles.container}>
          <Text style={styles.title}>{t('appError.title')}</Text>
          <Text style={styles.message}>{t('appError.message')}</Text>
          <Pressable style={styles.retryButton} onPress={this.handleRetry}>
            <Text style={styles.retryButtonText}>{t('appError.retry')}</Text>
          </Pressable>
        </View>
      );
    }

    return this.props.children;
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: appColors.background.app,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    gap: 12,
  },
  title: {
    fontSize: 18,
    fontWeight: '700',
    color: appColors.text.primary,
    textAlign: 'center',
  },
  message: {
    fontSize: 13,
    color: appColors.accent.primary,
    textAlign: 'center',
  },
  retryButton: {
    marginTop: 12,
    paddingVertical: 10,
    paddingHorizontal: 24,
    backgroundColor: appColors.border.strong,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: appColors.border.accent,
  },
  retryButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: appColors.text.primary,
    textAlign: 'center',
  },
});
